import type { ReactNode } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router'
import { AuthProvider, useAuth } from './auth/AuthProvider'
import { RequireAuth } from './auth/RequireAuth'
import { AppFrame } from './components/AppFrame'
import { AuthCallbackScreen } from './screens/AuthCallbackScreen'
import { NotAuthorizedScreen } from './screens/NotAuthorizedScreen'
import { NotFoundScreen } from './screens/NotFoundScreen'
import { OrderDetailScreen } from './screens/OrderDetailScreen'
import { OrdersScreen } from './screens/OrdersScreen'
import { ProductsScreen } from './screens/ProductsScreen'
import { SkuScreen } from './screens/SkuScreen'

/**
 * A gated page: session-gate must have returned 200 before anything inside
 * renders. The frame itself is inside the guard, not outside it — an
 * unauthorized visitor must never see the app chrome.
 */
function Gated({ children }: { children: ReactNode }) {
  return (
    <RequireAuth>
      <GatedFrame>{children}</GatedFrame>
    </RequireAuth>
  )
}

function GatedFrame({ children }: { children: ReactNode }) {
  const { state, signOut } = useAuth()
  const email = state.status === 'authorized' ? state.email : null

  return (
    <AppFrame accountLabel={email ?? undefined} onSignOut={signOut}>
      {children}
    </AppFrame>
  )
}

/**
 * Routing: browser history, relying on the Cloudflare Pages catch-all that
 * serves index.html with a 200 for every unmatched path.
 *
 * There is no `/sign-in` route by design. RequireAuth renders the sign-in
 * screen in place of the requested page, which is what lets the return URL be
 * "where you already are" rather than a query-parameter round trip.
 *
 * The `*` catch-all is task 2.1e. Pages answers every unmatched path with
 * index.html and a 200, so the server cannot report a bad URL — only this
 * route can. It must stay LAST: react-router picks the most specific match,
 * but keeping it last also keeps that obvious to the next reader.
 */
export function App() {
  return (
    <BrowserRouter>
      {/* AuthProvider sits inside the router because it navigates on the gate
          verdict and on the return-URL round trip. */}
      <AuthProvider>
        <Routes>
          <Route path="/auth/callback" element={<AuthCallbackScreen />} />
          <Route path="/not-authorized" element={<NotAuthorizedScreen />} />

          <Route
            path="/"
            element={
              <Gated>
                <OrdersScreen />
              </Gated>
            }
          />
          <Route
            path="/orders/:id"
            element={
              <Gated>
                <OrderDetailScreen />
              </Gated>
            }
          />
          <Route
            path="/products"
            element={
              <Gated>
                <ProductsScreen />
              </Gated>
            }
          />
          <Route
            path="/sku/:itemId"
            element={
              <Gated>
                <SkuScreen />
              </Gated>
            }
          />

          {/* Ungated on purpose: the URL does not exist signed in or out, and
              the screen shows no data. Gating it would turn a mistyped link
              into a sign-in prompt followed by a 404. */}
          <Route path="*" element={<NotFoundScreen />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
